import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import { SlideToggle } from '../SlideToggle';
import hooks from '@g4/automation-hooks';

import iconExpand from '../../../images/icon-24-expand-blue.svg';
import iconCollapse from '../../../images/icon-24-collapse-blue.svg';

export const Content = styled.div``;
export const InteractiveLabel = styled.a``;
export const NonInteractiveLabel = styled.span``;
export const LabelTitle = styled.span``;
export const StatusIndicator = styled.img``;

export const propTypes = {
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Determines the open or closed status of the Expando */
  open: PropTypes.bool,
  /** Description text to display on interactive part of component */
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  /** Determines if the Expando can be expanded or not */
  disabled: PropTypes.bool,
  /** Determines class attribute */
  className: PropTypes.string,
  /** Alternative text to be rendered when the status indicator icon cannot be rendered, due to: a slow connection, a bad `src` attribute, or if the user uses a screen reader */
  statusIndicatorIconAltAttribute: PropTypes.string.isRequired,
  /**
   * The ID of the content to create an aria relationship between (applied as `aria-controls`).
   * By default this relationship is created for you.
   */
  contentID: PropTypes.string,
  /** On toggle callback */
  onToggle: PropTypes.func,
  /** Callback for when expanding/collapsing animation finishes */
  onRest: PropTypes.func,
  /**
   * Hides the label.
   * Useful when needing to control the expanded state from elsewhere (in this case remember to set the `contentID`)
   */
  hideLabel: PropTypes.bool,
  children: PropTypes.node,
};

export const defaultProps = {
  open: false,
  disabled: false,
  hideLabel: false,
  direction: 'down',
  onToggle: Function.prototype,
  onRest: Function.prototype,
};

/** @visibleName Core Expando */
export class Expando extends PureComponent {
  static propTypes = propTypes;
  static defaultProps = defaultProps;

  constructor(props) {
    super(props);

    this.toggleContent = this.toggleContent.bind(this);

    this.contentID = props.contentID || _.uniqueId('Expando-');
  }

  toggleContent(e) {
    e.preventDefault();

    this.props.onToggle();
  }

  renderStatusIndicator(open) {
    if (this.props.disabled || this.props.hideLabel) {
      return null;
    }

    const icon = open ? iconCollapse : iconExpand;

    return (
      <StatusIndicator
        src={icon}
        alt={this.props.statusIndicatorIconAltAttribute}
      />
    );
  }

  renderNonInteractiveLabel() {
    return (
      <NonInteractiveLabel
        open={this.props.open}
        disabled={this.props.disabled}
      >
        <LabelTitle>{this.props.label}</LabelTitle>
      </NonInteractiveLabel>
    );
  }

  renderInteractiveLabel() {
    const { automationHook, open } = this.props;

    return (
      <InteractiveLabel
        href=""
        role="button"
        open={open}
        onClick={this.toggleContent}
        disabled={this.props.disabled}
        aria-expanded={this.props.open}
        aria-controls={this.contentID}
        {...hooks.hook(automationHook)}
      >
        <LabelTitle>{this.props.label}</LabelTitle>
        <div>{this.renderStatusIndicator(open)}</div>
      </InteractiveLabel>
    );
  }

  renderContent() {
    const children = React.Children.toArray(this.props.children);

    return (
      <SlideToggle open={this.props.open} onRest={this.props.onRest}>
        <Content
          id={this.contentID}
          aria-hidden={!this.props.open}
          hideLabel={this.props.hideLabel}
        >
          {children}
        </Content>
      </SlideToggle>
    );
  }

  render() {
    let expandoLabel;

    if (this.props.disabled) {
      expandoLabel = this.renderNonInteractiveLabel();
    } else {
      expandoLabel = this.renderInteractiveLabel();
    }

    return (
      <div className={this.props.className}>
        {this.props.hideLabel ? null : expandoLabel}
        {this.renderContent()}
      </div>
    );
  }
}

export default Expando;
